import React, { useContext, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import StarekContext from "../context/StarekContext";
import { jwtDecode } from "jwt-decode";
import ConfirmModal from "./modals/ConfirmModal";

function AuthGuardedRoute({ children }) {
    const starekContext = useContext(StarekContext);
    const location = useLocation();
    const [showModal, setShowModal] = useState(false);

    const isTokenValid = (token) => {
        if (!token) return false;
        try {
            const decoded = jwtDecode(token);
            if (!decoded.exp) return false;
            return Date.now() < decoded.exp * 1000;
        } catch {
            return false;
        }
    };

    useEffect(() => {
        setShowModal(
            !!starekContext?.auth && !isTokenValid(starekContext.auth)
        );
    }, [location, starekContext.auth]);

    if (!starekContext?.auth) {
        sessionStorage.setItem(
            "postAuthRedirect",
            location.pathname + location.search
        );
        return <Navigate to="/login" />;
    }

    if (showModal) {
        return (
            <>
                {children}
                <ConfirmModal
                    open={true}
                    type="warning"
                    title="Access Expired"
                    content="Your login access has expired or is invalid. Please login again."
                    showCloseButton={false}
                    customButtons={[{ label: "Login", action: "confirm" }]}
                    onConfirm={() => {
                        starekContext.setAuth(null);
                        window.location.href = "/login";
                    }}
                />
            </>
        );
    }

    return children;
}

function PlantChosenGuardedRoute({ children }) {
    const starekContext = useContext(StarekContext);
    const location = useLocation();

    const memberships = starekContext?.memberships ?? [];
    const lastUsedPlantId = starekContext?.lastUsedPlant?.id;

    if (!lastUsedPlantId) {
        sessionStorage.setItem(
            "postAuthRedirect",
            location.pathname + location.search
        );
        return <Navigate to="/choose-plant" replace />;
    }

    if (memberships.length > 0) {
        const hasAccess = memberships.some(
            (m) => (m.plant_id ?? m.plant?.id) === lastUsedPlantId
        );
        if (!hasAccess) {
            starekContext.setLastUsedPlant(null);
            sessionStorage.setItem(
                "postAuthRedirect",
                location.pathname + location.search
            );
            return <Navigate to="/choose-plant" replace />;
        }
    }
    return children;
}


function PermissionGuardedRoute({ entityCode, children }) {
    const { can, permissionMap } = useContext(StarekContext);
    const isLoaded = Object.keys(permissionMap).length > 0;

    if (isLoaded && !can(entityCode, "READ")) {
        return <Navigate to="/dashboard" replace />;
    }

    return children;
}

export { AuthGuardedRoute, PlantChosenGuardedRoute, PermissionGuardedRoute };
